// Styles
import { concatStyleSets, concatStyleSetsWithProps, memoizeFunction } from "@fluentui/react";
import { highContrastHighlightStyle, highContrastHighlightTextColor } from "../../../utilities/HighContrast.styles";
import { backgroundColorHover, backgroundColorNeutralPrimary, backgroundColorSelected, dialogControlSecondaryStyle, dialogControlStyle, flexParent, flexParentCenterAll, fontColorDisabled, fontColorNeutralPrimary, fontColorNeutralSecondary, selectedControlStyle } from "../../../utilities/Mixin.styles";
// Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { PlannerBorderRadius, PlannerInputHeight } from "../../../constants/StyleConstants";
// Utilities
import { isLoopTheme } from "../../../utilities/ThemingUtilities";
/**
 * Helper to get style function for the Text Field
 * @param theme App context theme
 * @param [isDisabled] Whether or not the dropdown is disabled
 * @param [customStyles] Optional set of custom styles that override the default styling
 */ export const getDropdownStyleFunction = memoizeFunction((theme, isDisabled, customStyles)=>(props)=>{
        return concatStyleSetsWithProps(props, getDropdownStyles(theme, isDisabled), customStyles);
    });
/**
 * The styles for the Dropdown
 * @param theme App context theme
 * @param isDisabled Whether or not the dropdown is disabled
 * @param [customStyles] Optional set of custom styles that override the default styling
 */ export const getDropdownStyles = memoizeFunction((theme, isDisabled, customStyles)=>{
    const isItLoopTheme = isLoopTheme(theme);
    let titlePaddingTop = "";
    let titleBorderRadius = "";
    let hoverColor = "";
    const calloutBorderRadius = PlannerBorderRadius;
    switch(theme.name){
        case PlannerTheme.Light:
            hoverColor = Colors.NeutralDark;
            titlePaddingTop = "1px";
            titleBorderRadius = "3px";
            break;
        case PlannerTheme.Dark:
            hoverColor = Colors.NeutralLight;
            titlePaddingTop = "1px";
            titleBorderRadius = "3px";
            break;
        case PlannerTheme.HighContrast:
            hoverColor = Colors.TeamsContrastHyperlinkColor;
            titlePaddingTop = "1px";
            titleBorderRadius = "3px";
            break;
        default:
            break;
    }
    const label = fontColorNeutralSecondary(theme);
    const dropdown = [
        fontColorNeutralPrimary(theme),
        isItLoopTheme ? dialogControlSecondaryStyle(theme) : dialogControlStyle(theme),
        {
            height: PlannerInputHeight,
            "> *": {
                backgroundColor: "transparent"
            },
            "&.is-open": selectedControlStyle(theme),
            "&:focus, &:hover": {
                ".ms-Dropdown-title": fontColorNeutralPrimary(theme),
                ".ms-Dropdown-caretDown": {
                    color: hoverColor
                }
            },
            "&:active": {
                ".ms-Dropdown-title": fontColorNeutralPrimary(theme)
            },
            "&:focus::after": {
                border: "none",
                position: "inherit"
            }
        },
        isDisabled && {
            "&:hover, &:focus, &:hover:focus": {
                ".ms-Dropdown-title, .ms-Dropdown-caretDown": fontColorDisabled(theme)
            }
        },
        !isDisabled && {
            "&:focus": [
                selectedControlStyle(theme)
            ]
        }
    ];
    const title = [
        isDisabled ? fontColorDisabled(theme) : fontColorNeutralPrimary(theme),
        {
            backgroundColor: "transparent",
            border: "0px solid transparent",
            borderRadius: titleBorderRadius,
            paddingTop: titlePaddingTop
        }
    ];
    const caretDownWrapper = [
        flexParent(),
        flexParentCenterAll
    ];
    const dropdownItem = [
        backgroundColorNeutralPrimary(theme),
        fontColorNeutralPrimary(theme),
        {
            border: "1px solid transparent",
            "&:focus, &:hover, &:hover:focus": [
                fontColorNeutralPrimary(theme),
                backgroundColorHover(theme),
                highContrastHighlightStyle(),
                {
                    ".icon span": highContrastHighlightStyle()
                },
                theme.name === PlannerTheme.Dark && {
                    backgroundColor: "inherit",
                    border: `1px solid ${Colors.White}`
                }
            ],
            ".ms-Button-flexContainer": {
                width: "100%"
            }
        }
    ];
    const dropdownItemSelected = [
        backgroundColorSelected(theme),
        fontColorNeutralPrimary(theme),
        {
            border: "1px solid transparent",
            "&:focus, &:hover, &:hover:focus": [
                fontColorNeutralPrimary(theme),
                backgroundColorHover(theme),
                theme.name === PlannerTheme.HighContrast && {
                    ".ms-Icon": {
                        color: Colors.Black
                    }
                },
                theme.name === PlannerTheme.Dark && {
                    backgroundColor: "inherit",
                    border: `1px solid ${Colors.White}`
                }
            ],
            ".ms-Button-flexContainer": {
                width: "100%"
            },
            ".plannerIcon > span": highContrastHighlightTextColor()
        },
        theme.name === PlannerTheme.HighContrast && {
            color: Colors.Black,
            ".ms-Icon": {
                color: Colors.Black
            }
        }
    ];
    const dropdownItems = [
        backgroundColorNeutralPrimary(theme),
        {
            borderTop: "1px solid transparent"
        }
    ];
    const callout = {
        borderRadius: calloutBorderRadius,
        maxHeight: "280px !important",
        ".ms-Callout-main": [
            {
                borderRadius: calloutBorderRadius
            },
            theme.name === PlannerTheme.HighContrast && {
                backgroundColor: Colors.Black
            }
        ]
    };
    return concatStyleSets({
        label,
        dropdown,
        title,
        caretDownWrapper,
        dropdownItem,
        dropdownItemSelected,
        dropdownItems,
        callout
    }, customStyles);
});
/**
 * The styles for the caret down icon
 * @param theme App context theme
 * @param isDisabled Whether or not the dropdown is disabled
 */ export const getCaretDownIconStyles = memoizeFunction((theme, isDisabled)=>{
    const root = [
        isDisabled ? fontColorDisabled(theme) : fontColorNeutralSecondary(theme)
    ];
    return {
        root
    };
});
